import apiClient from "../../../apis/api-client";
import {
  BaseApplicationService,
  SmartApplyApiError,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";
import {
  ProcedureEntity,
  ProcedureType,
} from "../../../domain/procedures/procedure-entity";
import { ApplicationEntity } from "../../../domain/application/application-entity";
import { RefundRequest } from "../../../domain/payment/refund-request";
import { PaymentOperation } from "../../../domain/payment/payment-operation";
import { toast } from "react-toastify";
import { gqlClient, gqlHeader, runGqlQueryV2 } from "@graffer-inc/smart-apply";
import { PaymentAdded } from "@graffer-inc/cloud-gov-core";
import { PaymentDetailByPaymentJSON } from "../../../domain/payment/payment-summary";
import { GrafferGqlResult } from "@graffer-inc/smart-apply";

type PageLoadingState = {
  isLoading: boolean;
  isRefunding: boolean;
  isWithdrawing: boolean;
};

export type PaymentDetailContextProps = {
  loadingStates: PageLoadingState;
  application?: ApplicationEntity;
  paymentIndex?: number;
  paymentOperations: PaymentOperation[];
  paymentDetail: GrafferGqlResult<PaymentDetailByPaymentJSON>;
  procedure?: ProcedureEntity;
  addedEvent?: PaymentAdded;
  onPageMount: typeof PaymentDetailService.prototype.onPageMount;
  refund: typeof PaymentDetailService.prototype.refund;
  withdraw: typeof PaymentDetailService.prototype.withdraw;
  adjustExceedingOrDeficiency: typeof PaymentDetailService.prototype.adjustExceedingOrDeficiency;
};

export class PaymentDetailService extends BaseApplicationService<PaymentDetailContextProps> {
  state = {
    // states
    loadingStates: {
      isLoading: false,
      isRefunding: false,
      isWithdrawing: false,
    },
    application: undefined as ApplicationEntity | undefined,
    paymentOperations: [],
    procedure: undefined,
    paymentIndex: undefined as number | undefined,
    paymentDetail: GrafferGqlResult.init(),
    addedEvent: undefined,
    // functions
    onPageMount: this.onPageMount.bind(this),
    refund: this.refund.bind(this),
    withdraw: this.withdraw.bind(this),
    adjustExceedingOrDeficiency: this.adjustExceedingOrDeficiency.bind(this),
  };

  async onPageMount(id: string, paymentIndex: number) {
    this.setLoadingState({ isLoading: true });
    try {
      const datas = await this.fetchData(id, paymentIndex);
      this.setState({
        application: datas.application,
        paymentIndex: paymentIndex,
        procedure: datas.procedure,
        paymentOperations: datas.operations,
        addedEvent: datas.foundEvent,
        paymentDetail: datas.paymentDetail,
      });
    } catch (e) {
      if (SmartApplyApiError.is404(e)) {
        // TODO: render 404.
        reportUnexpectedError(e);
        return;
      }
      reportUnexpectedError(e);
    } finally {
      this.setLoadingState({ isLoading: false });
    }
  }

  async refund(req: RefundRequest, refundReasons: string) {
    this.setLoadingState({ isRefunding: true });
    try {
      const { data } = await apiClient.refundPaymentsV2(
        this.state.application!.id,
        req.toAPI({
          paymentIndex: this.state.paymentIndex!,
          version: this.state.application!.version,
          message: refundReasons,
        }),
      );
      const datas = await this.fetchData(
        this.state.application!.id,
        this.state.paymentIndex!,
      );
      this.setState({
        application: data,
        paymentOperations: datas.operations,
        addedEvent: datas.foundEvent,
        paymentDetail: datas.paymentDetail,
      });
      toast.success("返金しました。");
      return true;
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return false;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      return false;
    } finally {
      this.setLoadingState({ isRefunding: false });
    }
  }

  async withdraw(paymentIndex: number) {
    try {
      const { data } = await apiClient.withdrawnPostPayment(
        this.state.application!.id,
        {
          paymentIndex: paymentIndex,
          version: this.state.application!.version,
        },
      );
      this.setState({
        application: data,
      });
      toast.success("決済を取下げました。");
      return true;
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return false;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      return false;
    }
  }

  async adjustExceedingOrDeficiency(paymentIndex: number): Promise<boolean> {
    try {
      const result = await runGqlQueryV2(() =>
        gqlClient.AdjustPaymentExceedingOrDeficiency(
          {
            applicationId: this.state.application!.id,
            version: this.state.application!.version.valueOf(),
            paymentIndex: paymentIndex,
          },
          gqlHeader("m"),
        ),
      );
      if (result.data?.adjustPaymentExceedingOrDeficiency) {
        toast.success("登録に成功しました。");
        await this.onPageMount(this.state.application!.id, paymentIndex);
        return true;
      } else {
        toast.error("登録に失敗しました。");
        return false;
      }
    } catch (e) {
      if (this.handleRetryableError(e)) {
        return false;
      }
      reportUnexpectedError(e);
      toast.error(
        "予期せぬエラーが発生しました。時間をおいて再度実行してください。",
      );
      return false;
    }
  }

  private setLoadingState(state: Partial<PageLoadingState>) {
    this.setState({
      loadingStates: {
        ...this.state.loadingStates,
        ...state,
      },
    });
  }

  private async fetchData(id: string, paymentIndex: number) {
    // MEMO(yo-tak): application も applicationPaymentDetailData のレスポンスに含めることもできるが、
    // ここでは対応しない。
    // レスポンスの支払い情報に含まれる InvoiceItemType の値が apiClient と gqlClient で異なるため、
    // コンポーネントとその利用箇所を調査、修正してからでないと切り替えられない。
    const { data: application } = await apiClient.fetchApplication(id);
    const { data: operations } =
      await apiClient.fetchApplicationPaymentOperations(id, paymentIndex);
    const payDetailData = await runGqlQueryV2(() =>
      gqlClient.applicationPaymentDetailData(
        { id: id, typedId: id },
        gqlHeader("q"),
      ),
    );
    if (payDetailData.error) {
      throw payDetailData.error;
    }
    if (!payDetailData.data) {
      throw new Error("データの取得に失敗しました");
    }
    const { applicationDetailData, applicationEventsAndMessages } =
      payDetailData.data;
    const procedure = ProcedureEntity.fromJSON(
      applicationDetailData.procedure as ProcedureType,
    );
    const foundEvent = applicationEventsAndMessages.events.find((e) => {
      return (
        e.eventType === "PaymentAdded" &&
        (e as PaymentAdded).data.paymentIndex === paymentIndex
      );
    });

    return {
      application,
      operations,
      procedure,
      foundEvent: foundEvent as PaymentAdded,
      paymentDetail: payDetailData.convert((data) => {
        return data?.paymentDetailByPayments.find(
          (d) =>
            d.paymentId === application.getPaymentsV2()[paymentIndex].paymentId,
        ) as PaymentDetailByPaymentJSON;
      }),
    };
  }
}
